/* Builder steps that set the sheet's numbers: traits (3), starting information (4)
   and starting equipment (5). */

import { ReactNode, useState } from 'react';
import { Backpack, Plus, Shield, ShieldOff, Swords, X } from 'lucide-react';
import { SIDECAR } from './content/sheetText';
import { Armor, BRAWLERS_STRIKE, Catalog, featureList, Weapon } from './catalog';
import { Character, deriveStats, freeTraitValues, itemName, kitItems, needs, noArmorCard, parseTraits, spellcastTrait, suggestedGear, Trait, TRAITS, TRAIT_VERBS } from './model';
import { capitalize, FeatureText, signed } from './SheetParts';
import { EvasionShield, SHIELD_PATH, TraitShield } from './svg';
import { Chips, Choice, Panel, Picker, Pips, StepProps, TextField, toggle } from './BuilderParts';
import LootPanel from './Loot';
import { IkonisPanel } from './MotherboardPanels';
import { ARMOR_RULE, equipWeapon, GearMove, SWAP_RULE, unequipWeapon } from './gear';


export function TraitsStep({ c, catalog, set }: StepProps) {
  // The token in hand, by its place in the pool, so only that one of two equal values lifts.
  const [heldAt, setHeldAt] = useState<number | null>(null);
  const cls = catalog.cls(c.className);
  const suggestion = cls?.stats?.suggestedTraits;
  const spellTrait = spellcastTrait(c, catalog);
  const free = freeTraitValues(c.traits).sort((a, b) => b - a);
  const held = heldAt === null ? null : free[heldAt] ?? null;

  const place = (t: Trait) => {
    const traits = { ...c.traits };
    if (held === null) delete traits[t];
    else traits[t] = held;
    setHeldAt(null);
    set({ traits });
  };

  return (
    <>
      <div className="cb-actions">
        {suggestion && (
          <button type="button" className="cb-btn cb-btn--primary" onClick={() => set({ traits: parseTraits(suggestion) })}>
            Use the {cls?.name} suggestion
          </button>
        )}
        <button type="button" className="cb-btn" onClick={() => set({ traits: {} })}>Clear traits</button>
      </div>
      <p className="cb-hint">Pick a value, then the trait to place it on. Tap a placed value to take it back.</p>
      <div className="cs-tokens">
        <div className="cb-pool" role="group" aria-label="Values to assign">
          {free.length === 0 && <span className="cb-hint">All six values are placed.</span>}
          {free.map((v, i) => (
            <button
              key={`${v}-${i}`}
              type="button"
              className={`cb-pool__value${heldAt === i ? ' is-held' : ''}`}
              aria-pressed={heldAt === i}
              onClick={() => setHeldAt(heldAt === i ? null : i)}
            >
              <svg viewBox="-1 -1 47 48" aria-hidden="true"><path d={SHIELD_PATH} /></svg>
              <span>{signed(v)}</span>
            </button>
          ))}
        </div>
        {/* The sheet's own trait row: each shield takes the value in hand */}
        <div className="cb-traits">
          {TRAITS.map((t) => {
            const value = signed(c.traits[t]);
            return (
              <TraitButton
                key={t}
                trait={t}
                value={value}
                className={held !== null && !value ? 'is-target' : undefined}
                label={`${capitalize(t)} ${value || 'empty'}${t === spellTrait ? ', spellcast trait' : ''}`}
                onClick={() => place(t)}
              >
                {t === spellTrait && <span className="cb-source" aria-hidden="true">Spellcast trait</span>}
              </TraitButton>
            );
          })}
        </div>
      </div>
    </>
  );
}

interface TraitButtonProps {
  trait: Trait;
  value: string;
  label: string;
  marked?: boolean;
  pressed?: boolean;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
  children?: ReactNode;
}

/** One of the sheet's trait shields as a button: the traits step places values on it, the level up raises it. Goes in a `.cs-tokens` `.cb-traits` row. */
export function TraitButton({ trait, value, label, marked, pressed, disabled, className, onClick, children }: TraitButtonProps) {
  return (
    <button type="button" className={['cb-trait', className].filter(Boolean).join(' ')} aria-label={label} aria-pressed={pressed} disabled={disabled} onClick={onClick}>
      <svg className="cs-svg" viewBox="-1 66 66 80" aria-hidden="true">
        <TraitShield x={0} label={trait.toUpperCase()} value={value} verbs={TRAIT_VERBS[trait]} marked={marked} />
      </svg>
      {children}
    </button>
  );
}

export function InfoStep({ c, catalog }: StepProps) {
  const cls = catalog.cls(c.className);
  if (!cls) return <p className="cb-hint">Choose a class first: it sets your starting Evasion and Hit Points.</p>;
  const stats = deriveStats(c, catalog);
  return (
    <div className="cb-info">
      <Panel title="Evasion">
        <div className="cb-info__row">
          <svg className="cs-svg cs-tokens cb-evasion" viewBox="10 74 58 70" role="img" aria-label={`Evasion ${stats.evasion}, starts at ${cls.stats?.evasion}`}>
            <EvasionShield value={stats.evasion ?? ''} start={cls.stats?.evasion} />
          </svg>
          <p>
            {cls.name}s start at <strong>{cls.stats?.evasion}</strong>
            {stats.evasion !== Number(cls.stats?.evasion) && <> and your armor or heritage make it <strong>{stats.evasion}</strong></>}.
          </p>
        </div>
        <p className="cb-hint">{SIDECAR.evasion}</p>
      </Panel>
      <Panel title="Hit Points and Stress">
        <div className="cb-info__track"><span>HP</span><Pips label="Hit Points" total={12} open={stats.hp} marked={0} shape="box" /></div>
        <div className="cb-info__track"><span>Stress</span><Pips label="Stress" total={12} open={stats.stress} marked={0} shape="box" /></div>
        <p className="cb-hint">You start with {stats.hp} Hit Point slots from your class and every character starts with {stats.stress} Stress slots. Dashed slots are gained as you level up.</p>
      </Panel>
      <Panel title="Hope">
        <Pips label="Hope" total={6} marked={stats.hope} shape="diamond" />
        <p className="cb-hint">{SIDECAR.hope}</p>
      </Panel>
    </div>
  );
}

const weaponStats = (w: Weapon) =>
  [w.trait || 'Trait of your choice', w.range, `${w.damage} ${w.damageType.startsWith('Mag') ? 'mag' : 'phy'}`, w.burden].filter(Boolean).join(' · ');

function WeaponChoice({ w, selected, suggested, disabled, onPick }: { w: Weapon; selected: boolean; suggested: boolean; disabled?: boolean; onPick: () => void }) {
  return (
    <Choice selected={selected} disabled={disabled} onClick={onPick} className="cb-gear">
      <span className="cb-choice__title">{w.name}</span>
      <span className="cb-choice__meta">{weaponStats(w)}</span>
      {w.feature && <span className="cb-choice__text"><FeatureText text={w.feature} /></span>}
      {suggested && <span className="cb-source">Suggested</span>}
    </Choice>
  );
}

function ArmorChoice({ a, selected, suggested, onPick }: { a: Armor; selected: boolean; suggested: boolean; onPick: () => void }) {
  return (
    <Choice selected={selected} onClick={onPick} className="cb-gear">
      <span className="cb-choice__title">{a.name}</span>
      <span className="cb-choice__meta">Thresholds {a.major}/{a.severe} · Score {a.score}</span>
      {a.feature && <span className="cb-choice__text"><FeatureText text={a.feature} /></span>}
      {suggested && <span className="cb-source">Suggested</span>}
    </Choice>
  );
}

const TIERS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'];

/** Which tier of a gear list is shown. */
function TierChips({ tier, onPick }: { tier: number; onPick: (tier: number) => void }) {
  return <Chips label="Tier" options={TIERS} isOn={(t) => t === TIERS[tier - 1]} onPick={(t) => onPick(TIERS.indexOf(t) + 1)} />;
}

/** Brawler's Strike has no tier, so it shows on every tier. */
const inTier = (tier: number) => (item: { tier?: number }) => !item.tier || item.tier === tier;

/** Primary weapons by type, as the equipment guide groups them. */
const primaryGroups = (catalog: Catalog): Array<[string, Weapon[]]> => [
  ['Physical', catalog.primaries.filter((w) => !w.damageType.startsWith('Mag'))],
  ['Magic', catalog.primaries.filter((w) => w.damageType.startsWith('Mag'))],
  ['Combat Wheelchairs', catalog.wheelchairs],
];

const asList = (v?: string | string[] | null) => (Array.isArray(v) ? v : v ? [v] : []);

function GearSummary({ w, trait }: { w: Weapon; trait?: string }) {
  return (
    <>
      <span className="cb-picked__name">{w.name}</span>
      <span className="cb-picked__meta">{weaponStats({ ...w, trait: w.trait || (trait ? capitalize(trait) : '') })}</span>
      {w.feature && <span className="cb-picked__meta"><FeatureText text={w.feature} /></span>}
    </>
  );
}

/** The trait Brawler's Strike uses, asked for wherever it is the primary weapon (this step and the quick character). */
export function StrikeTrait({ c, set }: { c: Character; set: StepProps['set'] }) {
  if (c.primary !== BRAWLERS_STRIKE.name) return null;
  return (
    <>
      <p className="cb-hint">Brawler's Strike uses a trait of your choice.</p>
      <Chips label="Brawler's Strike trait" options={TRAITS.map(capitalize)} isOn={(t) => t.toLowerCase() === c.strikeTrait} onPick={(t) => set({ strikeTrait: t.toLowerCase() as Trait })} />
    </>
  );
}

export function EquipmentStep({ c, catalog, set }: StepProps) {
  const cls = catalog.cls(c.className);
  const stats = cls?.stats ?? {};
  const suggested = [...asList(stats.suggestedPrimary), ...asList(stats.suggestedSecondary), stats.suggestedArmor ?? ''];
  const brawler = featureList(cls?.classFeatures).some((f) => f.name === 'I Am the Weapon');
  const primary = catalog.weapon(c.primary);
  const oneHanded = primary?.burden === 'One-Handed';
  const inv = cls?.inventory;
  const secondary = catalog.weapon(c.secondary);
  const armor = catalog.armor(c.armor);
  const groups: Array<[string, Weapon[]]> = [
    ...(brawler ? [["Brawler's Strike", [BRAWLERS_STRIKE]] as [string, Weapon[]]] : []),
    ...primaryGroups(catalog),
  ];
  const [primaryTier, setPrimaryTier] = useState(primary?.tier ?? 1);
  const [secondaryTier, setSecondaryTier] = useState(secondary?.tier ?? 1);
  const [armorTier, setArmorTier] = useState(armor?.tier ?? 1);
  const groupOf = (name: string) => groups.find(([, list]) => list.some((w) => w.name === name))?.[0];
  const [group, setGroup] = useState<string>();
  const shown = group ?? groupOf(c.primary) ?? groupOf(asList(stats.suggestedPrimary)[0] ?? '') ?? 'Physical';
  const pickPrimary = (w: Weapon) =>
    set({ primary: w.name, secondary: w.burden === 'One-Handed' ? c.secondary : '', strikeTrait: w === BRAWLERS_STRIKE ? c.strikeTrait : '' });


  return (
    <>
      {cls && (
        <div className="cb-actions">
          <button type="button" className="cb-btn cb-btn--primary" onClick={() => set(suggestedGear(c, catalog))}>Use the suggested {cls.name} gear</button>
        </div>
      )}
      <p className="cb-hint">Starting gear is Tier 1. Every tier is here for gear found, bought or won along the way.</p>
      <p className="cb-field__label">Weapons</p>
      <Chips label="Weapons" options={['Standard weapons', 'Ikonis']} isOn={(o) => (o === 'Ikonis') === c.useIkonis} onPick={(o) => set({ useIkonis: o === 'Ikonis' })} />
      <p className="cb-hint">An Ikonis replaces the primary and secondary weapons, as in the Motherboard campaign frame or your own game.</p>
      {c.useIkonis ? <IkonisPanel c={c} set={set} /> : (
      <>
      <Panel title="Primary weapon" needed={needs(c, 'primary weapon') || needs(c, "Brawler's Strike trait")}>
        <p className="cb-hint">Take either a two-handed primary weapon, or a one-handed primary weapon and a one-handed secondary weapon.</p>
        <Picker key={`p-${c.primary}`} what="primary weapon" picked={!!primary} summary={primary && <GearSummary w={primary} trait={c.strikeTrait} />}>
          {(close) => (
            <>
              <Chips label="Weapon type" options={groups.map(([name]) => name)} isOn={(name) => name === shown} onPick={setGroup} />
              <TierChips tier={primaryTier} onPick={setPrimaryTier} />
              <div className="cb-grid cb-grid--gear">
                {(groups.find(([name]) => name === shown)?.[1] ?? []).filter(inTier(primaryTier)).map((w) => (
                  <WeaponChoice key={w.name} w={w} selected={w.name === c.primary} suggested={suggested.includes(w.name)} onPick={() => { pickPrimary(w); close(); }} />
                ))}
              </div>
            </>
          )}
        </Picker>
        <StrikeTrait c={c} set={set} />
        {primary && primary !== BRAWLERS_STRIKE && <GearButton name={`Unequip ${primary.name}`} icon={Backpack} label="Unequip" move={unequipWeapon(c, 'primary')} set={set} />}
      </Panel>
      <Panel title="Secondary weapon">
        {!oneHanded ? (
          <p className="cb-hint">{primary ? `${primary.name} takes both hands, so there is no secondary weapon.` : 'Choose a one-handed primary weapon to free a hand for a secondary weapon.'}</p>
        ) : (
          <Picker key={`s-${c.secondary}`} what="secondary weapon" picked={!!secondary} summary={secondary && <GearSummary w={secondary} />}>
            {(close) => (
              <>
                <TierChips tier={secondaryTier} onPick={setSecondaryTier} />
                <div className="cb-grid cb-grid--gear">
                  <Choice selected={!c.secondary} onClick={() => { set({ secondary: '' }); close(); }} className="cb-gear">
                    <span className="cb-choice__title">None</span>
                    <span className="cb-choice__text">Keep a hand free.</span>
                  </Choice>
                  {catalog.secondaries.filter(inTier(secondaryTier)).map((w) => (
                    <WeaponChoice key={w.name} w={w} selected={w.name === c.secondary} suggested={suggested.includes(w.name)} onPick={() => { set({ secondary: w.name }); close(); }} />
                  ))}
                </div>
              </>
            )}
          </Picker>
        )}
        {secondary && <GearButton name={`Unequip ${secondary.name}`} icon={Backpack} label="Unequip" move={unequipWeapon(c, 'secondary')} set={set} />}
      </Panel>
      </>
      )}
      <Panel title="Armor" needed={needs(c, 'armor')}>
        <Picker
          key={`a-${c.armor}`}
          what="armor"
          picked={!!armor}
          summary={armor && (
            <>
              <span className="cb-picked__name">{armor.name}</span>
              <span className="cb-picked__meta">Thresholds {armor.major}/{armor.severe} · Score {armor.score}</span>
              {armor.feature && <span className="cb-picked__meta"><FeatureText text={armor.feature} /></span>}
              {c.armorOff && <span className="cb-picked__meta">Taken off: you are unarmored until you put it back on.</span>}
            </>
          )}
        >
          {(close) => (
            <>
              <TierChips tier={armorTier} onPick={setArmorTier} />
              <div className="cb-grid cb-grid--gear">
                {catalog.armors.filter(inTier(armorTier)).map((a) => <ArmorChoice key={a.name} a={a} selected={a.name === c.armor} suggested={suggested.includes(a.name)} onPick={() => { set({ armor: a.name, armorOff: false }); close(); }} />)}
              </div>
            </>
          )}
        </Picker>
        <NoArmorHint c={c} catalog={catalog} />
        {armor && (
          <>
            <GearButton
              name={`${c.armorOff ? 'Put on' : 'Take off'} ${armor.name}`}
              icon={c.armorOff ? Shield : ShieldOff}
              label={c.armorOff ? 'Put on' : 'Take off'}
              move={{ change: { armorOff: !c.armorOff } }}
              set={set}
            />
            <p className="cb-hint">{ARMOR_RULE}</p>
          </>
        )}
      </Panel>
      {inv && (
        <Panel title="Inventory" needed={needs(c, 'potion') || needs(c, 'class item')}>
          <StartingKit c={c} set={set} take={inv.take} />
          <p className="cb-field__label">Then choose between</p>
          <Chips label="Potion" options={inv.chooseBetween} display={itemName} isOn={(o) => o === c.potion} onPick={(potion) => set({ potion })} />
          <p className="cb-field__label">And either</p>
          <Chips label="Class item" options={inv.andEither} display={itemName} isOn={(o) => o === c.classItem} onPick={(classItem) => set({ classItem })} />
          {inv.thenDecideWhatYouUseForYourCraft && (
            <TextField
              label={inv.craftLabel ?? 'Then decide what you carry your spells in'}
              value={c.craft}
              placeholder={inv.thenDecideWhatYouUseForYourCraft.join(', ')}
              onChange={(craft) => set({ craft })}
            />
          )}
        </Panel>
      )}
      <LootPanel c={c} catalog={catalog} set={set} />
      <InventoryWeapons c={c} catalog={catalog} set={set} />
    </>
  );
}

/** The class's starting kit as chips like the loot's, each removable, and the removed ones ready to take back. */
function StartingKit({ c, set, take }: Pick<StepProps, 'c' | 'set'> & { take: string }) {
  const items = kitItems(take);
  const kept = items.filter((item) => !c.droppedKit.includes(item));
  const dropped = items.filter((item) => c.droppedKit.includes(item));
  const flip = (item: string) => set({ droppedKit: toggle(c.droppedKit, item) });
  return (
    <>
      <p className="cb-field__label">Take</p>
      {kept.length > 0 && (
        <ul className="cb-loot__taken cb-kit" aria-label="Starting kit">
          {kept.map((item) => (
            <li key={item} className="cb-loot__chip">
              <span className="cb-loot__gem" aria-hidden="true" />
              {itemName(item)}
              <button type="button" className="cb-loot__remove" aria-label={`Remove ${itemName(item)}`} onClick={() => flip(item)}>
                <X size={13} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {dropped.length > 0 && (
        <div className="cb-chips" role="group" aria-label="Removed from the kit">
          {dropped.map((item) => (
            <button key={item} type="button" className="cb-chip cb-kit__back" aria-label={`Take back ${itemName(item)}`} onClick={() => flip(item)}>
              <Plus size={13} aria-hidden="true" />{itemName(item)}
            </button>
          ))}
        </div>
      )}
      {/handful of gold/i.test(take) && <p className="cb-hint">And a handful of gold, already marked in your sheet's Gold.</p>}
    </>
  );
}

/** What a no-armor card (Bare Bones) gives while no armor is worn, or that the armor must come off to use it. */
function NoArmorHint({ c, catalog }: Pick<StepProps, 'c' | 'catalog'>) {
  const card = noArmorCard(c);
  if (!card) return null;
  const box = deriveStats(c, catalog).armorBox;
  if (box?.name !== card) return <p className="cb-hint">Take off your armor to use {card}.</p>;
  return <p className="cb-hint">{card}: without armor, your base Armor Score is {box.score} and your base thresholds are {box.major}/{box.severe}.</p>;
}

/** A move between worn and carried gear: its button, or the reason it cannot be made beside a disabled one. */
function GearButton({ name, icon: Icon, label, move, set }: { name: string; icon: typeof Plus; label: string; move: GearMove; set: StepProps['set'] }) {
  const reason = 'reason' in move ? move.reason : undefined;
  return (
    <div className="cb-gear-move">
      <button type="button" className="cb-btn" aria-label={name} disabled={!!reason} onClick={() => 'change' in move && set(move.change)}>
        <Icon size={15} aria-hidden="true" />{label}
      </button>
      {reason && <span className="cb-hint">{reason}</span>}
    </div>
  );
}

/** Up to two spare weapons of any tier, for the sheet's Inventory Weapon slots. */
function InventoryWeapons({ c, catalog, set }: StepProps) {
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState('Physical');
  const [tier, setTier] = useState(1);
  const groups: Array<[string, Weapon[]]> = [...primaryGroups(catalog), ['Secondary', catalog.secondaries]];
  const spares = c.inventoryWeapons.map((name) => catalog.weapon(name)).filter((w): w is Weapon => !!w);
  const take = (w: Weapon) => {
    set({ inventoryWeapons: [...c.inventoryWeapons, w.name] });
    setAdding(false);
  };
  return (
    <Panel title="Inventory weapons">
      <p className="cb-hint">Up to two spare weapons you carry but do not wield, from any tier. {SWAP_RULE}</p>
      {spares.map((w) => (
        <div key={w.name} className="cb-picked">
          <div className="cb-picked__body"><GearSummary w={w} /></div>
          {!c.useIkonis && <GearButton name={`Equip ${w.name}`} icon={Swords} label="Equip" move={equipWeapon(c, catalog, w.name)} set={set} />}
          <button
            type="button"
            className="cb-btn cb-picked__change"
            aria-label={`Remove ${w.name}`}
            onClick={() => set({ inventoryWeapons: c.inventoryWeapons.filter((n) => n !== w.name) })}
          >
            Remove
          </button>
        </div>
      ))}
      {spares.length < 2 && (adding ? (
        <>
          <Chips label="Weapon type" options={groups.map(([name]) => name)} isOn={(name) => name === type} onPick={setType} />
          <TierChips tier={tier} onPick={setTier} />
          <div className="cb-grid cb-grid--gear">
            {(groups.find(([name]) => name === type)?.[1] ?? [])
              .filter((w) => w.tier === tier && !c.inventoryWeapons.includes(w.name))
              .map((w) => <WeaponChoice key={w.name} w={w} selected={false} suggested={false} onPick={() => take(w)} />)}
          </div>
        </>
      ) : (
        <button type="button" className="cb-btn" onClick={() => setAdding(true)}>
          <Plus size={16} aria-hidden="true" />Add an inventory weapon
        </button>
      ))}
    </Panel>
  );
}
